import {
  databaseViewInitTemplate,
  provisionShardedDatabaseBlockDataSource,
} from '@blocksuite/affine-block-database';
import { deserializeXYWH, serializeXYWH } from '@blocksuite/global/gfx';
import type { BlockModel, Store } from '@blocksuite/store';
import {
  DEFAULT_CANVAS_DATABASE_HEIGHT,
  DEFAULT_CANVAS_DATABASE_WIDTH,
} from '@streamlain/database-identity';
import { PublicationError } from '@streamlain/doc-publication-contract';
import { autoMatchFields, checkCompatibility } from '@streamlain/field-types';

import type { OutputMapping, OutputTable } from '../services/automation';
import type {
  WorkflowSchema,
  WorkflowSchemaField,
} from '../services/workflow-client';
import { AUTOMATION_MAPPING_LIMIT } from './components/field-mapping-grid';

/** Free canvas space between the automation block and its incoming table. */
const INBOX_TABLE_GAP = 120;

export interface WebhookInboxTable {
  outputTable: OutputTable;
  outputMappings: OutputMapping[];
}

export class WebhookInboxMappingError extends Error {
  constructor() {
    super('Configure output fields to match the input fields.');
    this.name = 'WebhookInboxMappingError';
  }
}

/** Database column type for a workflow input field. */
function columnTypeOf(field: WorkflowSchemaField): string {
  return field.type === 'text' || field.type === 'json'
    ? 'rich-text'
    : field.type;
}

function inboxTablePosition(anchor: BlockModel): string | undefined {
  const xywh = (anchor.props as { xywh?: unknown }).xywh;
  if (typeof xywh !== 'string') return undefined;
  const [x, y, width] = deserializeXYWH(xywh);
  return serializeXYWH(
    x + width + INBOX_TABLE_GAP,
    y,
    DEFAULT_CANVAS_DATABASE_WIDTH,
    DEFAULT_CANVAS_DATABASE_HEIGHT
  );
}

/**
 * Creates the table a webhook automation stores its calls in: one column per
 * field of the workflow's start step, placed next to the automation block in
 * the same doc, and maps the workflow's outputs onto those columns by name.
 * The columns are written while the table is staged, so the table and its
 * schema land in one local creation group. The caller persists the returned
 * target and mappings.
 */
export async function createWebhookInboxTable(input: {
  doc: Store;
  docId: string;
  anchor: BlockModel;
  title: string;
  schema: WorkflowSchema;
}): Promise<WebhookInboxTable> {
  const fields = [
    ...input.schema.input.required,
    ...input.schema.input.optional,
  ];
  // Plan against stable schema IDs before the first document mutation. Never
  // create a partly mapped table or use start IDs as invented output IDs.
  const plannedColumns = fields.map(field => ({
    id: field.id,
    name: field.field,
    type: columnTypeOf(field),
  }));
  const outputFields = Object.values(input.schema.output).map(field => ({
    path: field.id,
    type: field.type,
    displayName: field.name,
  }));
  const plan = autoMatchFields(outputFields, plannedColumns);
  if (
    !fields.length ||
    fields.length > AUTOMATION_MAPPING_LIMIT ||
    new Set(fields.map(field => field.id)).size !== fields.length ||
    new Set(outputFields.map(field => field.path)).size !==
      outputFields.length ||
    plan.length !== fields.length ||
    plan.some(suggestion => {
      const chosen = suggestion.alternatives.find(
        candidate => candidate.responseField === suggestion.responseField
      );
      const column = plannedColumns.find(
        column => column.id === suggestion.targetColumnId
      )!;
      const output = outputFields.find(
        field => field.path === suggestion.responseField
      )!;
      // A greedy tie-break is useful in an editable preview, but must never
      // silently decide the schema of an automatically provisioned inbox.
      return (
        !chosen ||
        suggestion.alternatives.some(
          candidate =>
            candidate.responseField !== chosen.responseField &&
            candidate.score >= chosen.score
        ) ||
        checkCompatibility(column.type, output.type) === 'incompatible'
      );
    })
  )
    throw new WebhookInboxMappingError();

  const xywh = inboxTablePosition(input.anchor);
  const columns: { id: string; name: string; type: string }[] = [];

  const provisioned = await provisionShardedDatabaseBlockDataSource(input.doc, {
    title: input.title,
    parentId: input.doc.getParent(input.anchor)?.id,
    props: xywh ? { xywh } : undefined,
    initDataSource: dataSource => {
      databaseViewInitTemplate(dataSource, 'table');
      columns.length = 0;
      for (const field of fields) {
        const type = columnTypeOf(field);
        const id = dataSource.propertyAdd('end', { type, name: field.field });
        if (!id) {
          throw new Error(`Column "${field.field}" could not be created.`);
        }
        columns.push({ id, name: field.field, type });
      }
    },
  });
  if (provisioned.status === 'discarded')
    throw new PublicationError('CANCELLED');
  provisioned.dataSource.dispose();
  const databaseBlockId = provisioned.databaseBlockId;

  const columnIds = new Map(
    fields.map((field, i) => [field.id, columns[i].id])
  );
  const outputMappings = plan.map(suggestion => ({
    tableId: databaseBlockId,
    responseField: suggestion.responseField,
    responseFieldName: suggestion.responseFieldName,
    responseFieldType: outputFields.find(
      field => field.path === suggestion.responseField
    )!.type,
    targetColumnId: columnIds.get(suggestion.targetColumnId)!,
  }));

  return {
    outputTable: {
      tableId: databaseBlockId,
      tableName: input.title,
      docId: input.docId,
    },
    outputMappings,
  };
}
